import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import './Modals.css';

function ViewInquiriesModal({ onClose, inline = false, onPendingCountChange }) {
    const [inquiries, setInquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('ALL'); // ALL, NEW, FOLLOWED_UP, IN_PROGRESS
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showFollowUpModal, setShowFollowUpModal] = useState(false);
    const [selectedInquiry, setSelectedInquiry] = useState(null);
    const [followUpComment, setFollowUpComment] = useState('');
    const [keepOpen, setKeepOpen] = useState(false);

    useEffect(() => {
        fetchInquiries();
    }, []);

    useEffect(() => {
        if (onPendingCountChange) {
            const count = inquiries.filter(inq => inq.status === 'NEW' || inq.status === 'IN_PROGRESS').length;
            onPendingCountChange(count);
        }
    }, [inquiries]);

    const fetchInquiries = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getAllInquiries();
            setInquiries(response.data.data || []);
        } catch (error) {
            console.error('Error fetching inquiries:', error);
            setMessage({ type: 'error', text: 'Failed to load inquiries' });
        } finally {
            setLoading(false);
        }
    };

    const openFollowUpModal = (inquiry) => {
        setSelectedInquiry(inquiry);
        setFollowUpComment('');
        setKeepOpen(false);
        setShowFollowUpModal(true);
    };

    const handleFollowUp = async () => {
        if (!selectedInquiry) return;

        try {
            const status = keepOpen ? 'IN_PROGRESS' : 'FOLLOWED_UP';
            const token = localStorage.getItem('token');
            const API_URL = window.location.hostname === 'localhost'
                ? 'http://localhost:5001/api'
                : 'https://welittleleaf.com/api';

            const response = await fetch(`${API_URL}/admin/inquiries/${encodeURIComponent(selectedInquiry.inquiryId)}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    status,
                    comment: followUpComment
                })
            });

            const data = await response.json();

            if (data.success) {
                setMessage({ type: 'success', text: keepOpen ? 'Inquiry marked as in progress' : 'Inquiry marked as followed up' });

                // Update local state - add to follow-up history
                setInquiries(inquiries.map(inq => {
                    if (inq.inquiryId === selectedInquiry.inquiryId) {
                        const updatedHistory = [...(inq.followUpHistory || [])];
                        updatedHistory.push({
                            timestamp: new Date().toISOString(),
                            status: status,
                            comment: followUpComment || '',
                            adminAction: status === 'FOLLOWED_UP' ? 'Marked as Followed Up' : 'Marked as In Progress'
                        });

                        return {
                            ...inq,
                            status,
                            followedUpAt: new Date().toISOString(),
                            followUpHistory: updatedHistory
                        };
                    }
                    return inq;
                }));

                setShowFollowUpModal(false);
                setSelectedInquiry(null);
                setTimeout(() => setMessage({ type: '', text: '' }), 3000);
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to update inquiry status' });
            }
        } catch (error) {
            console.error('Error updating inquiry:', error);
            setMessage({ type: 'error', text: 'Failed to update inquiry status' });
        }
    };

    const handleMarkAdmitted = async (inquiry) => {
        if (!window.confirm(`Mark inquiry from ${inquiry.parentName} as admitted? This records today as the admission date.`)) return;

        try {
            const token = localStorage.getItem('token');
            const API_URL = window.location.hostname === 'localhost'
                ? 'http://localhost:5001/api'
                : 'https://welittleleaf.com/api';

            const response = await fetch(
                `${API_URL}/admin/inquiries/${encodeURIComponent(inquiry.inquiryId)}/status`,
                {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                    body: JSON.stringify({ status: 'ADMITTED' })
                }
            );
            const data = await response.json();
            if (data.success) {
                setMessage({ type: 'success', text: 'Inquiry marked as admitted' });
                setInquiries(prev => prev.map(inq =>
                    inq.inquiryId === inquiry.inquiryId
                        ? { ...inq, status: 'ADMITTED', admissionDate: new Date().toISOString().split('T')[0] }
                        : inq
                ));
                setTimeout(() => setMessage({ type: '', text: '' }), 3000);
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to update' });
            }
        } catch (error) {
            console.error('Error marking admitted:', error);
            setMessage({ type: 'error', text: 'Failed to update inquiry status' });
        }
    };

    const getFilteredInquiries = () => {
        if (filter === 'ALL') return inquiries;
        return inquiries.filter(inq => inq.status === filter);
    };

    const filteredInquiries = getFilteredInquiries();
    const newCount = inquiries.filter(inq => inq.status === 'NEW').length;
    const inProgressCount = inquiries.filter(inq => inq.status === 'IN_PROGRESS').length;
    const followedUpCount = inquiries.filter(inq => inq.status === 'FOLLOWED_UP').length;
    const admittedCount = inquiries.filter(inq => inq.status === 'ADMITTED').length;

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const body = (
        <>
            <div className={inline ? '' : 'modal-content large-modal'} onClick={(e) => e.stopPropagation()}>
                {!inline && (
                    <div className="modal-header">
                        <h2>Admission Inquiries</h2>
                        <button className="close-btn" onClick={onClose}>&times;</button>
                    </div>
                )}

                <div className="modal-body">
                    {/* Filter Tabs */}
                    <div className="filter-tabs">
                        <button
                            className={filter === 'ALL' ? 'active' : ''}
                            onClick={() => setFilter('ALL')}
                        >
                            All ({inquiries.length})
                        </button>
                        <button
                            className={filter === 'NEW' ? 'active' : ''}
                            onClick={() => setFilter('NEW')}
                        >
                            New ({newCount})
                        </button>
                        <button
                            className={filter === 'IN_PROGRESS' ? 'active' : ''}
                            onClick={() => setFilter('IN_PROGRESS')}
                        >
                            In Progress ({inProgressCount})
                        </button>
                        <button
                            className={filter === 'FOLLOWED_UP' ? 'active' : ''}
                            onClick={() => setFilter('FOLLOWED_UP')}
                        >
                            Followed Up ({followedUpCount})
                        </button>
                        <button
                            className={filter === 'ADMITTED' ? 'active' : ''}
                            onClick={() => setFilter('ADMITTED')}
                        >
                            Admitted ({admittedCount})
                        </button>
                    </div>

                    {message.text && (
                        <div className={`message ${message.type}`}>
                            {message.text}
                        </div>
                    )}

                    {loading ? (
                        <div className="loading">Loading inquiries...</div>
                    ) : filteredInquiries.length === 0 ? (
                        <div className="empty-state">
                            <p>No inquiries found</p>
                        </div>
                    ) : (
                        <div className="inquiries-list">
                            {filteredInquiries.map((inquiry) => (
                                <div key={inquiry.inquiryId} className={`inquiry-card ${inquiry.status.toLowerCase()}`}>
                                    <div className="inquiry-header">
                                        <div className="inquiry-info">
                                            <h3>{inquiry.studentName}</h3>
                                            <span className={`status-badge ${inquiry.status.toLowerCase()}`}>
                                                {inquiry.status === 'NEW' ? 'New' : inquiry.status === 'IN_PROGRESS' ? 'In Progress' : inquiry.status === 'ADMITTED' ? 'Admitted' : 'Followed Up'}
                                            </span>
                                        </div>
                                        <div className="inquiry-date">
                                            {formatDate(inquiry.submittedAt)}
                                        </div>
                                    </div>

                                    <div className="inquiry-details">
                                        <div className="detail-row">
                                            <div className="detail-item">
                                                <strong>Parent/Guardian:</strong>
                                                <span>{inquiry.parentName}</span>
                                            </div>
                                            <div className="detail-item">
                                                <strong>Student Age:</strong>
                                                <span>{inquiry.studentAge} years</span>
                                            </div>
                                        </div>

                                        <div className="detail-row">
                                            <div className="detail-item">
                                                <strong>Email:</strong>
                                                <a href={`mailto:${inquiry.email}`}>{inquiry.email}</a>
                                            </div>
                                            <div className="detail-item">
                                                <strong>Phone:</strong>
                                                <a href={`tel:${inquiry.phone}`}>{inquiry.phone}</a>
                                            </div>
                                        </div>

                                        <div className="detail-row">
                                            <div className="detail-item">
                                                <strong>Preferred Class:</strong>
                                                <span>{inquiry.preferredClass}</span>
                                            </div>
                                        </div>

                                        <div className="detail-row full-width">
                                            <div className="detail-item">
                                                <strong>Inquiry Message:</strong>
                                                <p className="inquiry-message">{inquiry.inquiry}</p>
                                            </div>
                                        </div>

                                        {inquiry.followUpHistory && inquiry.followUpHistory.length > 0 && (
                                            <div className="detail-row full-width">
                                                <div className="detail-item">
                                                    <strong>Follow-up History:</strong>
                                                    <div style={{marginTop: '10px', borderLeft: '3px solid #059669', paddingLeft: '15px'}}>
                                                        {inquiry.followUpHistory.map((history, index) => (
                                                            <div key={index} style={{marginBottom: '15px', paddingBottom: '15px', borderBottom: index < inquiry.followUpHistory.length - 1 ? '1px solid #e5e7eb' : 'none'}}>
                                                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px'}}>
                                                                    <span style={{fontWeight: '600', color: '#059669', fontSize: '0.9rem'}}>
                                                                        {history.adminAction}
                                                                    </span>
                                                                    <span style={{fontSize: '0.85rem', color: '#6b7280'}}>
                                                                        {formatDate(history.timestamp)}
                                                                    </span>
                                                                </div>
                                                                {history.comment && (
                                                                    <p style={{margin: '5px 0 0 0', fontStyle: 'italic', color: '#374151'}}>
                                                                        {history.comment}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div className="inquiry-actions">
                                        {(inquiry.status === 'NEW' || inquiry.status === 'IN_PROGRESS') && (
                                            <button
                                                className="btn btn-primary btn-sm"
                                                onClick={() => openFollowUpModal(inquiry)}
                                            >
                                                {inquiry.status === 'IN_PROGRESS' ? 'Update Follow-up' : 'Follow Up'}
                                            </button>
                                        )}
                                        {inquiry.status !== 'ADMITTED' && (
                                            <button
                                                onClick={() => handleMarkAdmitted(inquiry)}
                                                className="btn btn-primary btn-sm"
                                                style={{ fontSize: '0.8rem', padding: '4px 10px', background: '#16a34a', border: 'none' }}
                                            >
                                                ✓ Mark as Admitted
                                            </button>
                                        )}
                                        {inquiry.status === 'ADMITTED' && (
                                            <span style={{ color: '#16a34a', fontWeight: '600', fontSize: '0.875rem' }}>
                                                ✓ Admitted{inquiry.admissionDate ? ` on ${inquiry.admissionDate}` : ''}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Follow-up Modal */}
            {showFollowUpModal && selectedInquiry && (
                <div className="modal-overlay" onClick={() => setShowFollowUpModal(false)} style={{zIndex: 1001}}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{maxWidth: '500px'}}>
                        <div className="modal-header">
                            <h2>Follow Up on Inquiry</h2>
                            <button className="close-btn" onClick={() => setShowFollowUpModal(false)}>&times;</button>
                        </div>

                        <div className="modal-body">
                            <div style={{marginBottom: '20px', padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px'}}>
                                <strong>{selectedInquiry.studentName}</strong>
                                <div style={{fontSize: '0.9rem', color: '#6b7280', marginTop: '4px'}}>
                                    Parent: {selectedInquiry.parentName} | Phone: {selectedInquiry.phone}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Admin Comment</label>
                                <textarea
                                    value={followUpComment}
                                    onChange={(e) => setFollowUpComment(e.target.value)}
                                    placeholder="Add notes about this follow-up (optional)"
                                    rows="4"
                                    style={{width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #d1d5db'}}
                                />
                            </div>

                            <div className="form-group">
                                <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                    <input
                                        type="checkbox"
                                        id="keepOpen"
                                        checked={keepOpen}
                                        onChange={(e) => setKeepOpen(e.target.checked)}
                                        style={{width: 'auto', margin: 0}}
                                    />
                                    <label htmlFor="keepOpen" style={{margin: 0, fontWeight: '500'}}>
                                        Keep inquiry open (Mark as "In Progress")
                                    </label>
                                </div>
                                <small style={{color: '#6b7280', fontSize: '0.85rem', marginTop: '4px', display: 'block'}}>
                                    Check this if the inquiry requires additional follow-up
                                </small>
                            </div>

                            <div className="form-actions">
                                <button
                                    type="button"
                                    onClick={() => setShowFollowUpModal(false)}
                                    className="btn btn-secondary"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleFollowUp}
                                    className="btn btn-primary"
                                >
                                    {keepOpen ? 'Mark as In Progress' : 'Mark as Followed Up'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );

    if (inline) return body;

    return (
        <div className="modal-overlay" onClick={onClose}>
            {body}
        </div>
    );
}

export default ViewInquiriesModal;
